import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http, formatErr } from "../../lib/api";
import { fileToDataUrl, stripDataUrl, downloadB64 } from "../../lib/files";
import { usePolling } from "../../lib/usePolling";
import { useOpenParam } from "../../lib/useOpenParam";
import { pfield } from "../../lib/labels";
import { FilterBanner } from "../../components/FilterBanner";
import { MfaAdminReset } from "../../components/MfaAdminReset";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Upload, Download, Ban, Power } from "lucide-react";

const STATUS_LABEL = {
  profile_incomplete: "Profilo incompleto", candidatura_inviata: "Candidatura inviata",
  integrazioni_richieste: "Integrazioni richieste", candidatura_approvata: "Approvata",
  candidatura_rifiutata: "Rifiutata", pronto_attivazione: "Pronto attivazione",
  attivo: "Attivo", sospeso: "Sospeso", cessato: "Cessato",
};

const AGR_LABEL = { da_caricare: "Da caricare", inviato: "Inviato all'agente", firmato_da_verificare: "Firmato — da verificare", da_correggere: "Correzione richiesta", perfezionato: "Perfezionato" };
const bs = { borderColor: "var(--border-silver)" };

function nextStep(a) {
  if (["profile_incomplete", "integrazioni_richieste", "candidatura_rifiutata"].includes(a.status)) {
    if (!a.email_verified) return "L'agente deve verificare l'email (può richiedere un nuovo invio dalla sua dashboard).";
    if (!a.completeness?.complete) return "L'agente deve completare i dati mancanti del profilo.";
    return "L'agente deve inviare la candidatura dal proprio profilo.";
  }
  return { candidatura_inviata: "Valuta la candidatura: Approva o Rifiuta.", candidatura_approvata: "Carica l'accordo da far firmare all'agente.",
    pronto_attivazione: "Accordo perfezionato: attiva l'account.", attivo: "Nessuna azione richiesta.", sospeso: "Account sospeso: puoi riattivarlo." }[a.status] || "—";
}

export default function CrmVenditori() {
  const [agents, setAgents] = useState([]);
  const [sel, setSel] = useState(null);
  const [agr, setAgr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sp, setSp] = useSearchParams();
  const [flt, setFlt] = useState({ q: "", territory: "", status: sp.get("status") || "" });
  const statusFilter = sp.get("status");

  const load = async () => { const { data } = await http.get("/crm/agents"); setAgents(data); };
  useEffect(() => {
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  usePolling(load, { interval: 10000, enabled: true });

  const openAgent = async (a) => {
    setSel(a);
    try { const { data } = await http.get(`/crm/agents/${a.id}/agreement`); setAgr(data); } catch { setAgr(null); }
  };

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); await load(); if (sel) await openAgent({ id: sel.id }); toast.success(okMsg); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };

  const refreshSel = async (id) => {
    const { data } = await http.get(`/crm/agents/${id}`); setSel(data);
    try { const r = await http.get(`/crm/agents/${id}/agreement`); setAgr(r.data); } catch {}
  };

  const approve = (id) => act(() => http.post(`/crm/agents/${id}/approve`), "Candidatura approvata").then(() => refreshSel(id));
  const reject = (id) => act(() => http.post(`/crm/agents/${id}/reject`, { reason: "Non idoneo" }), "Candidatura rifiutata").then(() => refreshSel(id));
  const perfeziona = (id) => act(() => http.post(`/crm/agents/${id}/agreement/perfeziona`), "Accordo perfezionato").then(() => refreshSel(id));
  const requestCorr = (id) => act(() => http.post(`/crm/agents/${id}/agreement/request-correction`, { reason: "Firma mancante" }), "Correzione richiesta").then(() => refreshSel(id));
  const activate = (id) => act(() => http.post(`/crm/agents/${id}/activate`), "Account attivato").then(() => refreshSel(id));
  const suspend = (id) => act(() => http.post(`/crm/agents/${id}/suspend`, { reason: "Sospensione amministrativa" }), "Account sospeso").then(() => refreshSel(id));

  const uploadAgreement = async (id, e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const d = await fileToDataUrl(file);
      await http.post(`/crm/agents/${id}/agreement/upload`, { filename: file.name, data_b64: stripDataUrl(d), content_type: file.type || "application/pdf" });
      await refreshSel(id); await load(); toast.success("Accordo inviato all'agente.");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const original = agr?.versions?.filter((v) => v.kind === "original").slice(-1)[0];
  const signed = agr?.versions?.filter((v) => v.kind === "signed").slice(-1)[0];
  const norm = (s) => (s || "").toLowerCase().trim();
  const filtering = !!(flt.q || flt.territory || flt.status);
  const shownAgents = agents.filter((a) => {
    if (flt.status && a.status !== flt.status) return false;
    if (flt.territory && !norm(a.profile?.territory).includes(norm(flt.territory))) return false;
    if (flt.q) {
      const hay = norm([a.profile?.first_name, a.profile?.last_name, a.email].join(" "));
      if (!norm(flt.q).split(/\s+/).every((t) => hay.includes(t))) return false;
    }
    return true;
  });
  const territories = [...new Set(agents.map((a) => (a.profile?.territory || "").trim()).filter(Boolean))];
  const resetFilters = () => { setFlt({ q: "", territory: "", status: "" }); setSp({}); };
  const openId = useOpenParam(agents, openAgent);
  const emptyMsg = filtering ? "Nessun venditore corrisponde ai filtri." : "Nessun agente registrato.";

  return (
    <div data-testid="crm-venditori-page" className="max-w-6xl mx-auto">
      <h1 className="font-serif text-3xl mb-5">Venditori</h1>
      <FilterBanner label={statusFilter ? (STATUS_LABEL[statusFilter] || statusFilter) : null} onClear={resetFilters} />
      <div className="card-clean p-4 mb-5 grid grid-cols-1 md:grid-cols-4 gap-3 text-sm items-end" data-testid="crm-agents-filters">
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Nome o email</span><input value={flt.q} onChange={(e) => setFlt({ ...flt, q: e.target.value })} placeholder="Cerca…" data-testid="crm-agents-filter-q" className="w-full px-3 py-2 rounded-md border" style={bs} /></label>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Territorialità operativa</span><input list="crm-territories" value={flt.territory} onChange={(e) => setFlt({ ...flt, territory: e.target.value })} placeholder="Es. Milano" data-testid="crm-agents-filter-territory" className="w-full px-3 py-2 rounded-md border" style={bs} /><datalist id="crm-territories">{territories.map((t) => <option key={t} value={t} />)}</datalist></label>
        <label className="block"><span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Stato candidatura / account</span><select value={flt.status} onChange={(e) => setFlt({ ...flt, status: e.target.value })} data-testid="crm-agents-filter-status" className="w-full px-3 py-2 rounded-md border" style={bs}><option value="">Tutti</option>{Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <div className="flex items-center gap-3"><button onClick={resetFilters} disabled={!filtering} data-testid="crm-agents-filter-reset" className="px-4 py-2 rounded-md border text-sm disabled:opacity-40" style={bs}>Azzera filtri</button><span className="text-xs text-neutral-500" data-testid="crm-agents-count">{shownAgents.length} di {agents.length}</span></div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="card-clean overflow-hidden lg:col-span-2" data-testid="crm-agents-list">
          <table className="w-full text-sm hidden md:table">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b" style={{ borderColor: "var(--border-silver)" }}>
              <th className="px-4 py-3">Agente</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Stato</th>
            </tr></thead>
            <tbody>
              {shownAgents.length === 0 && <tr><td colSpan={3} className="px-4 py-6 text-neutral-500" data-testid="crm-agents-empty">{emptyMsg}</td></tr>}
              {shownAgents.map((a) => (
                <tr key={a.id} id={`open-${a.id}`} onClick={() => openAgent(a)} data-testid={`crm-agent-row-${a.id}`}
                  className={`border-b last:border-0 cursor-pointer hover:bg-neutral-50 ${sel?.id === a.id || openId === a.id ? "bg-neutral-50" : ""}`} style={{ borderColor: "var(--border-silver)" }}>
                  <td className="px-4 py-3 flex items-center gap-2">
                    {a.profile?.photo_url ? <img src={a.profile.photo_url} alt="" className="w-7 h-7 rounded-full object-cover" /> : <span className="w-7 h-7 rounded-full bg-neutral-200 inline-block" />}
                    {a.profile?.first_name || "—"} {a.profile?.last_name || ""}
                  </td>
                  <td className="px-4 py-3 text-neutral-500">{a.email}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded" style={{ background: "var(--bg-main)" }}>{STATUS_LABEL[a.status] || a.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="md:hidden divide-y" data-testid="crm-agents-cards">
            {shownAgents.length === 0 && <div className="px-4 py-6 text-sm text-neutral-500">{emptyMsg}</div>}
            {shownAgents.map((a) => (
              <button key={a.id} onClick={() => openAgent(a)} data-testid={`crm-agent-card-${a.id}`}
                className={`w-full text-left px-4 py-3 flex items-center gap-3 ${sel?.id === a.id ? "bg-neutral-50" : ""}`}>
                {a.profile?.photo_url ? <img src={a.profile.photo_url} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" /> : <span className="w-9 h-9 rounded-full bg-neutral-200 inline-block shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{a.profile?.first_name || "—"} {a.profile?.last_name || ""}</div>
                  <div className="text-xs text-neutral-500 truncate">{a.email}</div>
                </div>
                <span className="text-[10px] px-2 py-1 rounded shrink-0 whitespace-nowrap" style={{ background: "var(--bg-main)" }}>{STATUS_LABEL[a.status] || a.status}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="card-clean p-5" data-testid="crm-agent-detail">
          {!sel ? <p className="text-sm text-neutral-500" data-testid="crm-agent-detail-empty">{shownAgents.length === 0 ? emptyMsg : "Seleziona un agente dall'elenco."}</p> : (
            <>
              <div className="flex items-center gap-3 mb-4">
                {sel.profile?.photo_url ? <img src={sel.profile.photo_url} alt="" className="w-14 h-14 rounded-full object-cover" /> : <span className="w-14 h-14 rounded-full bg-neutral-200 inline-block" />}
                <div>
                  <div className="font-semibold">{sel.profile?.first_name} {sel.profile?.last_name}</div>
                  <div className="text-xs text-neutral-500">{sel.email}</div>
                  <div className="text-xs mt-1" data-testid="crm-agent-detail-status"><b>{STATUS_LABEL[sel.status] || sel.status}</b></div>
                </div>
              </div>
              <div className="text-xs text-neutral-500 space-y-1 mb-4">
                <div>Telefono: {sel.profile?.phone || "—"}</div>
                <div>Territorialità: {sel.profile?.territory || "—"}</div>
                <div>Preferenza: {sel.commission_pref || "—"}</div>
                <div data-testid="crm-agent-email-verified">Email verificata: <b className={sel.email_verified ? "text-green-700" : "text-rose-700"}>{sel.email_verified ? "sì" : "no"}</b></div>
              </div>
              <div className="text-xs rounded-md p-3 mb-4 space-y-1" style={{ background: "var(--bg-main)" }} data-testid="crm-agent-requirements">
                <div>Dati mancanti: <b data-testid="crm-agent-missing">{sel.completeness?.missing?.length ? sel.completeness.missing.map(pfield).join(", ") : "nessuno"}</b></div>
                <div>Prossimo passo: <b data-testid="crm-agent-next-step">{nextStep(sel)}</b></div>
                {["profile_incomplete", "integrazioni_richieste"].includes(sel.status) && <div className="text-neutral-500">Approvazione e attivazione non disponibili finché l'agente non verifica l'email, completa il profilo e invia la candidatura.</div>}
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {sel.status === "candidatura_inviata" && <>
                  <Btn onClick={() => approve(sel.id)} testid="crm-approve" icon={CheckCircle2}>Approva</Btn>
                  <Btn onClick={() => reject(sel.id)} testid="crm-reject" icon={XCircle} variant="danger">Rifiuta</Btn>
                </>}
                {sel.status === "pronto_attivazione" && <Btn onClick={() => activate(sel.id)} testid="crm-activate" icon={Power}>Attiva account</Btn>}
                {sel.status === "sospeso" && <Btn onClick={() => activate(sel.id)} testid="crm-reactivate" icon={Power}>Riattiva</Btn>}
                {sel.status === "attivo" && <Btn onClick={() => suspend(sel.id)} testid="crm-suspend" icon={Ban} variant="danger">Sospendi</Btn>}
                {["attivo", "sospeso", "pronto_attivazione", "candidatura_approvata"].includes(sel.status) && <MfaAdminReset agent={sel} />}
              </div>

              {/* Accordo */}
              {["candidatura_approvata", "pronto_attivazione", "sospeso", "attivo"].includes(sel.status) && (
                <div className="border-t pt-4" style={{ borderColor: "var(--border-silver)" }} data-testid="crm-agreement-block">
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2" data-testid="crm-agreement-status">Accordo · {AGR_LABEL[agr?.status || "da_caricare"] || agr?.status}</div>
                  <div className="space-y-2">
                    {sel.status !== "attivo" && agr?.status !== "perfezionato" && <label className="px-3 py-2 rounded-md text-white text-xs flex items-center gap-2 cursor-pointer w-fit" style={{ background: "var(--brand-dark)" }} data-testid="crm-agreement-upload-label">
                      <Upload className="w-3 h-3" /> {original ? "Ricarica" : "Carica"} accordo
                      <input type="file" className="hidden" onChange={(e) => uploadAgreement(sel.id, e)} data-testid="crm-agreement-upload-input" />
                    </label>}
                    {original && <button onClick={() => downloadB64(original.data_b64, original.filename, original.content_type)} className="px-3 py-2 rounded-md border text-xs flex items-center gap-2" style={{ borderColor: "var(--border-silver)" }} data-testid="crm-agreement-download-original"><Download className="w-3 h-3" /> Scarica originale</button>}
                    {signed && <button onClick={() => downloadB64(signed.data_b64, signed.filename, signed.content_type)} className="px-3 py-2 rounded-md border text-xs flex items-center gap-2" style={{ borderColor: "var(--border-silver)" }} data-testid="crm-agreement-download-signed"><Download className="w-3 h-3" /> Scarica copia firmata</button>}
                    {agr?.status === "firmato_da_verificare" && <>
                      <Btn onClick={() => perfeziona(sel.id)} testid="crm-agreement-perfeziona" icon={CheckCircle2}>Perfeziona accordo</Btn>
                      <Btn onClick={() => requestCorr(sel.id)} testid="crm-agreement-correction" variant="danger">Richiedi correzione</Btn>
                    </>}
                  </div>
                  <p className="text-[10px] text-neutral-400 mt-2">L'attivazione è possibile solo con accordo perfezionato.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Btn({ children, onClick, testid, icon: Icon, variant }) {
  const style = variant === "danger" ? { borderColor: "#e11d48", color: "#be123c" } : { background: "var(--brand-primary)", color: "white" };
  const cls = variant === "danger" ? "border" : "";
  return <button onClick={onClick} data-testid={testid} className={`px-3 py-2 rounded-md text-xs flex items-center gap-1 ${cls}`} style={style}>{Icon && <Icon className="w-3 h-3" />}{children}</button>;
}
